import type { DocumentVisibility, PlanDocument } from "@/engine/types";
import { can, type MemberPlanRole, type PlanActor, type PlanLifecycle } from "@/features/access/can";

export type DocReader = {
  role: MemberPlanRole;
  /** 작성자 비교에 쓰는 현재 사람 이름. */
  actorId: string;
  /** 로컬 파일은 공개와 비공개를 가리지 않는다. */
  host: "local" | "cloud";
  status?: PlanLifecycle;
  /** 클라우드에서 작성자가 아직 플랜 멤버인지. */
  authorOnPlan?: (authorId: string) => boolean;
  /** 기여자가 담당인 실행 작업 id. */
  ownExecutionTaskIds?: readonly string[];
};

function actorOf(reader: DocReader): PlanActor {
  return { role: reader.role, capabilities: [] };
}

function planAllows(reader: DocReader, actionId: string): boolean {
  return can(actorOf(reader), actionId, { status: reader.status ?? "active" });
}

export function canReadDocument(doc: PlanDocument, reader: DocReader): boolean {
  if (doc.deletedAt) return false;
  if (reader.host === "local") return planAllows(reader, "plan.doc.read") || reader.role === "owner";
  if (!planAllows(reader, "plan.doc.read")) return false;
  if (doc.visibility === "private" && doc.authorId !== reader.actorId) return orphanNeedsPromotion(doc, reader);
  return true;
}

/** 본문·첨부·검색 텍스트. 떠난 작성자의 나만 문서는 소유자에게 제목만 남긴다. */
export function canReadDocumentContent(doc: PlanDocument, reader: DocReader): boolean {
  return canReadDocument(doc, reader) && !orphanNeedsPromotion(doc, reader);
}

export function canEditDocument(doc: PlanDocument, reader: DocReader, linkedExecutionTaskIds: readonly string[] = []): boolean {
  if (doc.deletedAt) return false;
  if ((reader.status ?? "active") === "archived") return false;
  if (!planAllows(reader, "plan.doc.edit")) return false;
  if (reader.host === "cloud" && doc.visibility === "private" && doc.authorId !== reader.actorId) return false;
  if (reader.role !== "contributor") return true;
  if (doc.authorId === reader.actorId) return true;
  const own = new Set(reader.ownExecutionTaskIds ?? []);
  return linkedExecutionTaskIds.some((id) => own.has(id));
}

export function canDeleteDocument(doc: PlanDocument, reader: DocReader, linkedExecutionTaskIds: readonly string[] = []): boolean {
  if (!canEditDocument(doc, reader, linkedExecutionTaskIds)) return false;
  return planAllows(reader, "plan.doc.delete");
}

/** 나만으로 좁히는 주체는 작성자다. 편집자가 타인 글을 걷어 내지 못한다. */
export function canMakePrivate(doc: PlanDocument, reader: DocReader): boolean {
  return doc.authorId === reader.actorId && canEditDocument(doc, reader);
}

/** 영구 삭제는 소유자만. 아카이브에서는 읽기만 남긴다. */
export function canPurgeDocument(doc: PlanDocument, reader: DocReader): boolean {
  if (!doc.deletedAt) return false;
  if ((reader.status ?? "active") === "archived") return false;
  return reader.role === "owner";
}

export function canRestoreDocument(doc: PlanDocument, reader: DocReader): boolean {
  if (!doc.deletedAt) return false;
  if ((reader.status ?? "active") === "archived") return false;
  if (!planAllows(reader, "plan.doc.restore")) return false;
  if (doc.authorId === reader.actorId) return true;
  const onPlan = reader.authorOnPlan?.(doc.authorId) ?? true;
  return reader.role === "owner" && !onPlan;
}

export function canSeeTrashRow(doc: PlanDocument, reader: DocReader): boolean {
  if (!doc.deletedAt) return false;
  if (doc.authorId === reader.actorId) return true;
  const onPlan = reader.authorOnPlan?.(doc.authorId) ?? true;
  return reader.role === "owner" && !onPlan;
}

/** 작성자가 떠난 나만 문서는 소유자에게 제목만 보인다. 본문은 공개 전환 뒤에 연다. */
export function redactOrphanBody(doc: PlanDocument, reader: DocReader): PlanDocument {
  if (reader.host !== "cloud") return doc;
  if (doc.visibility !== "private" || doc.authorId === reader.actorId) return doc;
  if (reader.role !== "owner") return doc;
  const onPlan = reader.authorOnPlan?.(doc.authorId) ?? true;
  if (onPlan) return doc;
  return { ...doc, blocks: [], plainText: "", comments: [], attachments: [] };
}

export function orphanNeedsPromotion(doc: PlanDocument, reader: DocReader): boolean {
  if (reader.host !== "cloud" || doc.visibility !== "private" || doc.authorId === reader.actorId) return false;
  if (reader.role !== "owner") return false;
  return reader.authorOnPlan?.(doc.authorId) === false;
}

const LOCAL_VISIBILITY_NOTE = "이 파일에서는 공개와 비공개가 가려지지 않습니다. 워크스페이스로 올리면 적용됩니다.";

export function visibilityCopy(value: DocumentVisibility, host: "local" | "cloud"): { title: string; body: string; localNote?: string } {
  if (value === "private") {
    return {
      title: "비공개",
      body: "작성자만 봅니다.",
      ...(host === "local" ? { localNote: LOCAL_VISIBILITY_NOTE } : {}),
    };
  }
  return {
    title: "공개",
    body: "이 플랜을 볼 수 있는 멤버에게 보입니다.",
    ...(host === "local" ? { localNote: LOCAL_VISIBILITY_NOTE } : {}),
  };
}
